import React, { useLayoutEffect, useRef, useState } from 'react';
import { TenderDocumentKind, ServiceAssignment } from '../../types';
import { alignLvRows, extractTenderLV, extractTenderRequirements } from '../../services/geminiService';
import { uploadTenderDocument } from '../../services/crmService';
import {
  TenderLvRow,
  TenderLvRowDraft,
  TenderLvIssue,
  checkTenderLvRows,
  normalizeTenderLvRows,
  parseTenderQuantity,
  reapplyTenderLvRows,
  summarizeTenderLvRows,
  tenderLvRowKey,
} from '../../services/tenderLvImport';
import { Button } from '../Button';
import { LvMarkupView } from './LvMarkupView';
import { MarkedLvRow } from '../../services/pdf/lvManualMarking';
import { buildLvAlignPrompt, mergeAlignedRows, toAlignableRows } from '../../services/pdf/lvRowAlignment';
import {
  X, Upload, Loader2, AlertTriangle, FileText, Lock, CheckCircle2, Trash2, ArrowUp, ArrowDown, Info,
  Highlighter,
} from 'lucide-react';

// Der Typ lebt jetzt im Service (dort haengt die gesamte Aufbereitung), wird
// aber weiter von hier re-exportiert — der QuoteEditor importiert ihn so.
export type { TenderLvRow } from '../../services/tenderLvImport';

// Auswahl fuers Review-Dropdown. Werte = ServiceAssignment aus types.ts.
const ASSIGNMENTS: { value: ServiceAssignment; label: string }[] = [
  { value: 'none', label: 'Keine Zuordnung' },
  { value: 'inspection', label: 'Begehung' },
  { value: 'sigeplan', label: 'SiGe-Plan' },
  { value: 'asplan', label: 'A+S-Plan (kontaminierte Bereiche)' },
  { value: 'einweisung', label: 'Einweisung SiGe-Plan' },
  { value: 'unterlage', label: 'Unterlage (RAB 32)' },
  { value: 'vorank', label: 'Vorankündigung' },
  { value: 'site_rules', label: 'Baustellenordnung' },
  { value: 'planning_general', label: 'Planung allgemein' },
  { value: 'additional', label: 'Sonstiges / Nebenkosten' },
  { value: 'gbu', label: 'Gefährdungsbeurteilung (SiFa)' },
];

// Aus der Ausschreibung ausgelesene Projekt-Rahmendaten (Funktion 2). Alle
// Felder optional — der Nutzer waehlt im Review, was uebernommen wird.
export interface TenderRequirements {
  title?: string;
  siteStreet?: string;
  siteZip?: string;
  siteCity?: string;
  projectStart?: string;
  projectEnd?: string;
  intervalLabel?: string;
  inspectionCount?: number;
  tenderRequirements?: string;
}

/** Second argument of `onApplyLv`. */
export interface TenderLvApplyMeta {
  tenderDocumentId: string | null;
}

interface Props {
  quoteId: string;
  kind: TenderDocumentKind;
  /**
   * Wiedererkennungsschluessel der bereits im Angebot stehenden Positionen
   * (quoteItemLvKey). Ein zweiter Import derselben Datei haekelt die schon
   * vorhandenen Zeilen ab, statt das Angebot zu verdoppeln.
   */
  existingKeys?: ReadonlySet<string>;
  /**
   * Keys of the positions that are already in the quote but STRUCK. A struck
   * position keeps counting as present: its row cannot be selected here, so a
   * second import neither heals the strike nor adds the line twice.
   */
  excludedKeys?: ReadonlySet<string>;
  onClose: () => void;
  /**
   * `meta.tenderDocumentId`: id of the tender_documents row uploaded by THIS
   * import (null when the upload failed). The editor stores it as
   * `details.activeTenderDocumentId` so the PDF attaches exactly this
   * original — not merely the newest upload, which may come from a cancelled
   * or wrong import (audit 24.09., Q10).
   */
  onApplyLv: (rows: TenderLvRow[], meta: TenderLvApplyMeta) => void;
  onApplyRequirements: (req: TenderRequirements) => void;
}

// Inline-base64 an Gemini hat eine Payload-Grenze (~20 MB). Gesendet wird aber
// nicht die Datei, sondern ihre base64-Fassung — und die ist rund ein Drittel
// groesser. Die alte 18-MB-Grenze liess deshalb Dateien durch, die als ~24-MB-
// Payload in einen rohen API-Fehler liefen statt in diese klare Vorabmeldung.
const MAX_BYTES = 13 * 1024 * 1024;
const MAX_MB_LABEL = '13 MB';
const ACCEPT = 'application/pdf,image/*';

/**
 * Nur was das Modell wirklich lesen kann. GAEB (.x8*, .d8*, .p8*), Excel und
 * CSV wurden bisher als "application/pdf" deklariert durchgereicht — die KI
 * bekam Binaermuell und erfand daraus Positionen.
 */
const SUPPORTED = (file: File): boolean =>
  file.type === 'application/pdf' || file.type.startsWith('image/');

const UNSUPPORTED_HINT =
  'Dieses Format kann nicht gelesen werden. Unterstützt werden PDF und Bilder (Scan/Foto). '
  + 'GAEB-, Excel- und CSV-Dateien bitte vorher als PDF ausgeben.';

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error || new Error('Datei konnte nicht gelesen werden.'));
    reader.readAsDataURL(file);
  });

const inputClass =
  'w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all disabled:opacity-60';
const labelClass = 'text-[11px] font-bold text-slate-500 uppercase tracking-widest';

/**
 * Der Positionstext ist das, wonach im Review ueberhaupt entschieden wird — in
 * einem einzeiligen <input> war er nach rund 30 Zeichen abgeschnitten und nur
 * noch durch Scrollen im Feld lesbar. Das Feld waechst deshalb mit dem Inhalt.
 * Zeilenumbrueche bleiben draussen: der Text laeuft so, wie er hier steht, in
 * die Angebotsposition und ins PDF.
 */
const AutoGrowTextarea: React.FC<{
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
}> = ({ value, onChange, className, placeholder }) => {
  const ref = useRef<HTMLTextAreaElement | null>(null);

  const fit = (el: HTMLTextAreaElement) => {
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };

  useLayoutEffect(() => {
    if (ref.current) fit(ref.current);
  }, [value]);

  // Breitenaenderung (Fenster, Aufklappen der Spalten) aendert den Umbruch und
  // damit die noetige Hoehe. Nur auf Breite reagieren, sonst dreht sich der
  // Beobachter an der selbst gesetzten Hoehe im Kreis.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    let lastWidth = el.offsetWidth;
    const ro = new ResizeObserver(() => {
      if (el.offsetWidth === lastWidth) return;
      lastWidth = el.offsetWidth;
      fit(el);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <textarea
      ref={ref}
      rows={1}
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/[\r\n]+/g, ' '))}
      onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
      className={className}
      placeholder={placeholder}
      title={value || undefined}
    />
  );
};

/**
 * `mengeInput` ist die ROHE Eingabe des Mengenfeldes. Das Feld war
 * `type="number"`; in einem de-DE-Browser liefert eine Komma-Eingabe dort ''
 * und die Korrektur setzte die Menge still auf 0 — derselbe Fehler wie damals
 * bei den Mindeststunden. Jetzt Text, geparst ueber parseTenderQuantity.
 */
type LvRowState = TenderLvRowDraft & { id: string; mengeInput?: string };
type ReqFieldKey = keyof TenderRequirements;

/** Kennzeichnung unvollstaendiger, aber uebernehmbarer Zeilen. */
const REVIEW_BADGE = { label: 'prüfen', className: 'bg-orange-50 text-orange-700 border-orange-200' };

/** Kennzeichnung der nicht vorausgewaehlten Zeilen im Review. */
const KIND_BADGE: Record<TenderLvRowDraft['kind'], { label: string; className: string } | null> = {
  service: null,
  heading: { label: 'Überschrift', className: 'bg-blue-50 text-blue-600 border-blue-200' },
  sum: { label: 'Summe', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  empty: { label: 'leer', className: 'bg-slate-100 text-slate-500 border-slate-200' },
};

export const TenderImportModal: React.FC<Props> = ({ quoteId, kind, existingKeys, excludedKeys, onClose, onApplyLv, onApplyRequirements }) => {
  const isLv = kind === 'lv';
  const fileRef = useRef<HTMLInputElement>(null);

  const [phase, setPhase] = useState<'select' | 'loading' | 'marking' | 'review' | 'error'>('select');
  // Welchen Weg hat der Nutzer im Auswahlbildschirm gewaehlt? Beide benutzen
  // denselben Dateidialog, deshalb muss die Absicht vorher feststehen.
  const pickModeRef = useRef<'ai' | 'mark'>('ai');
  const [markFile, setMarkFile] = useState<File | null>(null);
  // Zieht der Nutzer gerade eine Datei ueber die Auswahlflaeche?
  const [dragOver, setDragOver] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Der Upload des Originals ist non-blocking: schlaegt er fehl, koennen die
  // extrahierten Daten trotzdem uebernommen werden (Warnung im Review).
  const [uploadWarn, setUploadWarn] = useState<string | null>(null);
  /** Meldung der KI-Ausricht-Stufe bzw. des Scan-Fallbacks — getrennt von der Upload-Warnung. */
  const [modeNote, setModeNote] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  // Upload of the original for the CURRENT file. Resolves to the new
  // document id, or null on failure — never rejects. Replaced per file, so a
  // cancelled earlier attempt is never the one handed to the editor.
  const uploadRef = useRef<Promise<string | null>>(Promise.resolve(null));

  const [lvRows, setLvRows] = useState<LvRowState[]>([]);
  // Die KI-Antwort brach mitten im Dokument ab: das Ergebnis ist unvollstaendig.
  const [truncated, setTruncated] = useState(false);
  const [req, setReq] = useState<TenderRequirements>({});
  const [reqAccept, setReqAccept] = useState<Record<ReqFieldKey, boolean>>({} as Record<ReqFieldKey, boolean>);

  const runExtraction = async (file: File) => {
    setLoadingLabel(`KI analysiert „${file.name}“…`);
    setPhase('loading');
    setError(null);
    setUploadWarn(null);
    setTruncated(false);
    setFileName(file.name);
    try {
      const base64 = await fileToBase64(file);
      // Original persistieren + KI-Extraktion parallel. Upload-Fehler blockiert
      // die Extraktion nicht (allSettled).
      const upload = uploadTenderDocument(quoteId, kind, file);
      uploadRef.current = upload.then((d) => d.id, () => null);
      const [uploadRes, extractRes] = await Promise.allSettled([
        upload,
        isLv ? extractTenderLV(base64, file.type) : extractTenderRequirements(base64, file.type),
      ]);

      if (uploadRes.status === 'rejected') {
        setUploadWarn('Original-PDF konnte nicht gespeichert werden (Extraktion trotzdem moeglich).');
        console.error('Tender-Upload fehlgeschlagen:', uploadRes.reason);
      }

      if (extractRes.status === 'rejected') {
        throw extractRes.reason;
      }

      if (isLv) {
        const { rows, truncated: cut } = extractRes.value as { rows: any[]; truncated: boolean };
        if (!rows?.length) {
          throw new Error(cut
            ? 'Die KI-Antwort wurde abgeschnitten, bevor eine Position lesbar war. Bitte das LV aufteilen und die Teile einzeln einlesen.'
            : 'Keine Positionen erkannt. Ist das Dokument ein Leistungsverzeichnis?');
        }
        setTruncated(cut);
        // Einstufung, Abschnitts-Vererbung und Duplikat-Erkennung liegen im
        // Service — das Modal zeigt nur noch, was dabei herauskam.
        setLvRows(normalizeTenderLvRows(rows, existingKeys ?? new Set()).map((r) => ({
          ...r,
          id: crypto.randomUUID(),
        })));
      } else {
        const d = (extractRes.value as Record<string, any>) || {};
        const next: TenderRequirements = {
          title: d.bauvorhaben ? String(d.bauvorhaben) : undefined,
          siteStreet: d.strasse ? String(d.strasse) : undefined,
          siteZip: d.plz ? String(d.plz) : undefined,
          siteCity: d.ort ? String(d.ort) : undefined,
          projectStart: d.baubeginn ? String(d.baubeginn) : undefined,
          projectEnd: d.bauende ? String(d.bauende) : undefined,
          intervalLabel: d.intervall ? String(d.intervall) : undefined,
          inspectionCount: Number.isFinite(Number(d.begehungen)) && Number(d.begehungen) > 0 ? Number(d.begehungen) : undefined,
          tenderRequirements: d.anforderungen ? String(d.anforderungen) : undefined,
        };
        setReq(next);
        // Standardmaessig alle Felder mit Wert zur Uebernahme markiert.
        const accept = {} as Record<ReqFieldKey, boolean>;
        (Object.keys(next) as ReqFieldKey[]).forEach((k) => { accept[k] = next[k] !== undefined && next[k] !== ''; });
        setReqAccept(accept);
      }
      setPhase('review');
    } catch (e) {
      setError((e as Error)?.message || 'Die KI-Analyse ist fehlgeschlagen.');
      setPhase('error');
    }
  };

  const handleFile = (file?: File | null) => {
    if (!file) return;
    if (!SUPPORTED(file)) {
      setError(UNSUPPORTED_HINT);
      setPhase('error');
      return;
    }
    // Selbst markieren: das PDF wird lokal gelesen, es geht nichts an die KI.
    // Die Groessengrenze der Inline-Payload gilt hier deshalb nicht.
    if (pickModeRef.current === 'mark' && isLv) {
      if (file.type !== 'application/pdf') {
        setError('Markieren geht nur in einer PDF mit Textebene. Ein Foto oder Scan muss über die KI laufen.');
        setPhase('error');
        return;
      }
      setFileName(file.name);
      setMarkFile(file);
      setError(null);
      setUploadWarn(null);
      setTruncated(false);
      setPhase('marking');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(`Datei ist zu gross (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximal ${MAX_MB_LABEL} — bitte kleiner scannen oder aufteilen. (Die Datei wird zum Senden kodiert und dabei um ein Drittel groesser.)`);
      setPhase('error');
      return;
    }
    runExtraction(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    // Nur Dateien annehmen. Markierter Text oder ein Link darf die Flaeche
    // nicht aufleuchten lassen.
    if (!Array.from(e.dataTransfer.types || []).includes('Files')) return;
    e.preventDefault();
    setDragOver(true);
  };

  /**
   * Ablegen per Maus. Der Dateidialog kennt zwei Wege (KI oder selbst
   * markieren); eine gezogene Datei sagt nicht, welcher gemeint ist. Sie geht
   * deshalb immer den KI-Weg — der Markiermodus bleibt der Knopf darunter.
   */
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (!files?.length) return;
    if (files.length > 1) {
      setError('Bitte nur eine Datei ablegen — eingelesen wird immer ein Dokument.');
      setPhase('error');
      return;
    }
    pickModeRef.current = 'ai';
    handleFile(files[0]);
  };

  /**
   * Uebernahme aus dem Markiermodus.
   *
   * Die markierten Zeilen sind bereits ein vollstaendiges Ergebnis — die
   * KI-Stufe richtet sie nur noch aus (Ordnungsnummer aus dem Text loesen,
   * Menge/Einheit trennen, Abschnitt und Leistungsart ergaenzen). Sie darf
   * deshalb fehlschlagen, ohne dass etwas verloren geht: `mergeAlignedRows`
   * faellt fuer jede nicht beantwortete Zeile auf die lokale Spaltenlesung
   * zurueck.
   */
  const applyMarkedRows = async (marked: MarkedLvRow[]) => {
    setLoadingLabel('KI richtet die markierten Positionen aus…');
    setPhase('loading');
    const alignable = toAlignableRows(marked);

    // Original mitspeichern, damit "bepreist anhaengen" spaeter funktioniert.
    if (markFile) {
      uploadRef.current = uploadTenderDocument(quoteId, kind, markFile).then((d) => d.id, (e) => {
        setUploadWarn('Original-PDF konnte nicht gespeichert werden (die Positionen sind trotzdem übernommen).');
        console.error('Tender-Upload fehlgeschlagen:', e);
        return null;
      });
    } else {
      uploadRef.current = Promise.resolve(null);
    }

    let aligned;
    try {
      const res = await alignLvRows(buildLvAlignPrompt(alignable));
      aligned = mergeAlignedRows(res, alignable);
    } catch (e) {
      console.warn('LV-Ausrichtung durch die KI fehlgeschlagen — markierte Zeilen werden unveraendert uebernommen.', e);
      setModeNote('Die KI-Ausrichtung war nicht erreichbar — die markierten Zeilen wurden unverändert übernommen. Bitte die Leistungsart je Position selbst setzen.');
      aligned = mergeAlignedRows(null, alignable);
    }

    setLvRows(normalizeTenderLvRows(aligned, existingKeys ?? new Set()).map((r) => ({
      ...r,
      id: crypto.randomUUID(),
    })));
    setMarkFile(null);
    setPhase('review');
  };

  // ─── LV-Review-Aktionen ─────────────────────────────────────────────────────

  /**
   * Felder, die die Einstufung und damit die Abschnitts-Vererbung veraendern.
   * Wird eines davon korrigiert, muss die ganze Liste neu abgeleitet werden:
   * traegt der Nutzer bei einer als Ueberschrift eingestuften Pauschalposition
   * "psch" nach, war sie vorher weiterhin eine Ueberschrift UND alle Folge-
   * zeilen behielten den von ihr falsch gesetzten Abschnitt.
   */
  const STRUCTURAL: (keyof LvRowState)[] = ['bezeichnung', 'einheit', 'menge', 'position', 'abschnitt'];

  const updateLvRow = (id: string, patch: Partial<LvRowState>) =>
    setLvRows((rows) => {
      // Der Abschnitt ist ein ABGELEITETER Wert (geerbt). Wer ihn von Hand
      // setzt, meint ihn als Vorgabe — sonst wuerde die Neuableitung die
      // Eingabe im selben Zug wieder ueberschreiben. Als eigenes Feld und
      // nicht ueber sourceAbschnitt, weil auch das LEEREN eine Entscheidung
      // ist: sonst erbt die Zeile sofort wieder den Titel von oben.
      const withOverride: Partial<LvRowState> = patch.abschnitt !== undefined
        ? { ...patch, abschnittOverride: patch.abschnitt }
        : patch;
      // Ebenso beim Haken: eine Handentscheidung ueberlebt jede spaetere
      // Neuableitung, eine bloss gerechnete Vorauswahl nicht.
      const effective: Partial<LvRowState> = patch.selected !== undefined
        ? { ...withOverride, selectionTouched: true }
        : withOverride;
      const next = rows.map((r) => (r.id === id ? { ...r, ...effective } : r));
      const structural = STRUCTURAL.some((k) => k in effective);
      return structural ? reapplyTenderLvRows(next, existingKeys ?? new Set()) : next;
    });
  const removeLvRow = (id: string) => setLvRows((rows) => rows.filter((r) => r.id !== id));
  /**
   * Zeile im Review umsortieren. Die Uebernahme behaelt die Reihenfolge bei,
   * und aus ihr entsteht spaeter die Positionsnummer — hat die KI eine Zeile an
   * die falsche Stelle gesetzt, wird sie hier geradegerueckt, bevor sie
   * ueberhaupt ins Angebot geht.
   */
  const moveLvRow = (id: string, dir: -1 | 1) =>
    setLvRows((rows) => {
      const i = rows.findIndex((r) => r.id === id);
      const to = i + dir;
      if (i < 0 || to < 0 || to >= rows.length) return rows;
      const next = [...rows];
      [next[i], next[to]] = [next[to], next[i]];
      return next;
    });
  /** Row already in the quote as a STRUCK position (live key — the review is editable). */
  const isStruck = (r: TenderLvRowDraft): boolean => !!excludedKeys?.has(tenderLvRowKey(r));
  const selectedCount = lvRows.filter((r) => r.selected && !isStruck(r)).length;
  const struckCount = lvRows.filter(isStruck).length;
  const summary = summarizeTenderLvRows(lvRows);
  // A struck row is ALWAYS a duplicate as well — it does sit in the quote.
  // Netted out here so the hint reports it once (as struck) instead of twice.
  const duplicateCount = lvRows.filter((r) => r.duplicate && !isStruck(r)).length;
  // Plausibilitaet: Luecken in der Ordnungszahl-Folge, Rueckwaertssprunge,
  // doppelte Nummern. Das ist die einzige Moeglichkeit, ein verschlucktes
  // Dokumentstueck OHNE das Original zu bemerken.
  const issues: TenderLvIssue[] = isLv ? checkTenderLvRows(lvRows) : [];

  const applyLv = async () => {
    // The marking path uploads in the background; wait for its id so the
    // editor can pin exactly this original.
    const tenderDocumentId = await uploadRef.current;
    const rows = lvRows
      // A struck position stays struck: it is not offered for selection and is
      // filtered out here as well, in case an edit turned the row into a match
      // after the selection was made.
      .filter((r) => r.selected && !isStruck(r))
      .map(({ position, bezeichnung, einheit, menge, abschnitt, assignment, nep }) => ({ position, bezeichnung, einheit, menge, abschnitt, assignment, nep }));
    onApplyLv(rows, { tenderDocumentId });
    onClose();
  };

  const applyRequirements = () => {
    const out: TenderRequirements = {};
    (Object.keys(req) as ReqFieldKey[]).forEach((k) => {
      if (reqAccept[k] && req[k] !== undefined && req[k] !== '') {
        (out as any)[k] = req[k];
      }
    });
    onApplyRequirements(out);
    onClose();
  };

  const title = isLv ? 'LV aus Ausschreibung übernehmen' : 'Anforderungen aus Ausschreibung auslesen';
  const subtitle = isLv
    ? 'Positionen, Mengen und Abschnitte werden als gesperrte LV-Zeilen übernommen — Einheitspreise trägst du selbst ein, Beschreibung und NEP bleiben änderbar.'
    : 'Projekt-Rahmendaten und Anforderungen werden ausgelesen. Es werden keine Positionen oder Preise erzeugt.';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" onMouseDown={onClose}>
      <div
        className={`w-full ${phase === 'marking' ? 'max-w-6xl' : phase === 'review' && isLv ? 'max-w-5xl' : 'max-w-3xl'} max-h-[90vh] flex flex-col bg-white rounded-3xl shadow-2xl overflow-hidden transition-[max-width] duration-200`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Kopf */}
        <div className="flex items-start justify-between gap-3 px-6 py-5 border-b border-slate-100">
          <div className="flex items-start gap-3">
            <div className="p-2.5 bg-blue-50 rounded-xl text-blue-600 shrink-0"><FileText className="w-5 h-5" /></div>
            <div>
              <h2 className="text-base font-bold text-slate-800">{title}</h2>
              <p className="text-xs text-slate-500 mt-0.5 max-w-xl">{subtitle}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-all shrink-0" title="Schließen">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Inhalt */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {(phase === 'select' || phase === 'error') && (
            <div
              className="space-y-4"
              onDragOver={handleDragOver}
              onDragLeave={(e) => {
                // Kinder feuern beim Ueberfahren eigene dragleave-Ereignisse:
                // nur zuruecksetzen, wenn der Zeiger die Flaeche wirklich verlaesst.
                if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
                setDragOver(false);
              }}
              onDrop={handleDrop}
            >
              {phase === 'error' && error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              <button
                onClick={() => { pickModeRef.current = 'ai'; fileRef.current?.click(); }}
                className={`w-full flex flex-col items-center justify-center gap-3 py-12 border-2 border-dashed rounded-2xl transition-all ${
                  dragOver
                    ? 'border-blue-500 bg-blue-50 text-blue-600'
                    : 'border-slate-200 text-slate-400 hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50/40'
                }`}
              >
                <Upload className="w-8 h-8" />
                <span className="text-sm font-semibold">
                  {dragOver ? 'Datei hier loslassen' : 'Ausschreibung hierher ziehen oder wählen (PDF oder Bild)'}
                </span>
                <span className="text-xs">Die KI liest die Positionen selbst heraus · max. {MAX_MB_LABEL}</span>
              </button>

              {/* Der Notausgang. Wenn die KI-Extraktion daneben liegt, bestimmt
                  der Nutzer die Positionen selbst — die KI richtet danach nur
                  noch aus, statt zu suchen. */}
              {isLv && (
                <button
                  onClick={() => { pickModeRef.current = 'mark'; fileRef.current?.click(); }}
                  className="w-full flex items-center gap-3 p-4 border border-slate-200 rounded-2xl text-left hover:border-blue-400 hover:bg-blue-50/40 transition-all group"
                >
                  <span className="p-2.5 bg-amber-50 text-amber-600 rounded-xl shrink-0 group-hover:bg-amber-100 transition-all">
                    <Highlighter className="w-5 h-5" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-slate-700">Positionen selbst im PDF markieren</span>
                    <span className="block text-xs text-slate-500 mt-0.5">
                      Für LVs, bei denen die KI Positionen übersieht oder verschiebt: Zeilen im Original
                      markieren, die KI richtet sie danach nur noch in Felder aus. Braucht eine PDF mit
                      Textebene (kein reiner Scan).
                    </span>
                  </span>
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept={ACCEPT}
                className="hidden"
                onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
              />
            </div>
          )}

          {phase === 'loading' && (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-slate-500">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              <span className="text-sm font-semibold">{loadingLabel || `KI analysiert „${fileName}“…`}</span>
              <span className="text-xs">Das kann bei mehrseitigen Ausschreibungen etwas dauern.</span>
            </div>
          )}

          {phase === 'marking' && markFile && (
            <LvMarkupView
              file={markFile}
              onApply={applyMarkedRows}
              onCancel={() => { setMarkFile(null); setPhase('select'); }}
              onNoTextLayer={() => {
                // Reiner Scan: markieren geht nicht, aber der KI-Weg schon.
                const f = markFile;
                setMarkFile(null);
                pickModeRef.current = 'ai';
                if (f && f.size > MAX_BYTES) {
                  // Der Markierpfad kennt keine Groessengrenze (er liest lokal).
                  // Der KI-Ausweg schon — sonst laeuft ein 40-MB-Scan in einen
                  // rohen API-Fehler statt in diese klare Meldung.
                  setError(
                    `Diese PDF hat keine Textebene (reiner Scan) und ist mit ${(f.size / 1024 / 1024).toFixed(1)} MB `
                    + `zu groß für die KI-Analyse. Maximal ${MAX_MB_LABEL} — bitte kleiner scannen oder aufteilen.`,
                  );
                  setPhase('error');
                } else if (f) {
                  runExtraction(f);
                  // NACH runExtraction: das setzt uploadWarn synchron auf null,
                  // und modeNote wird dort nicht angefasst.
                  setModeNote('Diese PDF hat keine Textebene (reiner Scan) — Markieren ist dort nicht möglich, es läuft stattdessen die KI-Extraktion.');
                } else {
                  setPhase('select');
                }
              }}
            />
          )}

          {phase === 'review' && uploadWarn && (
            <div className="flex items-start gap-2 p-3 mb-4 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{uploadWarn}</span>
            </div>
          )}

          {phase === 'review' && modeNote && (
            <div className="flex items-start gap-2 p-3 mb-4 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-600">
              <Info className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />
              <span>{modeNote}</span>
            </div>
          )}

          {/* Review — LV */}
          {phase === 'review' && isLv && (
            <div className="space-y-2">
              {truncated && (
                <div className="flex items-start gap-2 p-3 mb-2 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    <strong>Unvollständig:</strong> die KI-Antwort wurde abgeschnitten — das Dokument ist länger
                    als das, was hier steht. Bitte die letzte Position mit dem Original abgleichen und den Rest
                    aus einer aufgeteilten Datei nachladen.
                  </span>
                </div>
              )}
              {issues.length > 0 && (
                <div className="space-y-1.5 mb-2">
                  {issues.map((iss, i) => (
                    <div
                      key={i}
                      className={`flex items-start gap-2 p-2.5 rounded-xl text-xs border ${
                        iss.severity === 'warn'
                          ? 'bg-amber-50 border-amber-200 text-amber-800'
                          : 'bg-slate-50 border-slate-200 text-slate-600'
                      }`}
                    >
                      {iss.severity === 'warn'
                        ? <AlertTriangle className="w-4 h-4 mt-px shrink-0" />
                        : <Info className="w-4 h-4 mt-px shrink-0 text-slate-400" />}
                      <span>{iss.text}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-2 text-xs text-slate-500 px-1">
                <Lock className="w-3.5 h-3.5" />
                <span>{selectedCount} von {lvRows.length} Positionen ausgewählt. Bezeichnung/Einheit/Menge sind korrigierbar — Preise trägst du danach im Angebot ein.</span>
              </div>
              {(summary.headings > 0 || summary.sums > 0 || duplicateCount > 0 || struckCount > 0) && (
                <div className="flex items-start gap-2 p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-600">
                  <FileText className="w-4 h-4 mt-0.5 shrink-0 text-slate-400" />
                  <span>
                    Nicht vorausgewählt:{' '}
                    {[
                      summary.headings ? `${summary.headings} Überschrift(en) — sie werden stattdessen zum Abschnitt der folgenden Positionen` : '',
                      summary.sums ? `${summary.sums} Summen-/Übertragszeile(n)` : '',
                      duplicateCount ? `${duplicateCount} bereits im Angebot vorhandene Position(en)` : '',
                      struckCount ? `${struckCount} im Angebot gestrichene Position(en) — sie bleiben gestrichen und sind hier nicht wählbar` : '',
                    ].filter(Boolean).join(' · ')}
                    . Anhaken übernimmt die übrigen trotzdem.
                  </span>
                </div>
              )}
              <div className="hidden md:grid grid-cols-[28px_70px_minmax(0,2fr)_70px_70px_minmax(0,1fr)_28px] gap-2 px-2 text-[11px] uppercase font-bold text-slate-400 tracking-wider">
                <span></span><span>LV-Pos</span><span>Bezeichnung</span><span>Einh.</span><span>Menge</span><span>Abschnitt</span><span></span>
              </div>
              {lvRows.map((r) => {
                const struck = isStruck(r);
                return (
                  <div key={r.id} className={`grid grid-cols-2 md:grid-cols-[28px_70px_minmax(0,2fr)_70px_70px_minmax(0,1fr)_28px] gap-2 items-start p-2 rounded-xl border ${r.selected && !struck ? 'bg-slate-50 border-slate-100' : 'bg-white border-slate-100 opacity-50'}`}>
                    <input
                      type="checkbox"
                      checked={r.selected && !struck}
                      disabled={struck}
                      onChange={(e) => updateLvRow(r.id, { selected: e.target.checked })}
                      className="w-4 h-4 mt-2.5 self-start rounded border-slate-300 text-blue-600 focus:ring-blue-400/40 disabled:opacity-40"
                      title={struck ? 'Steht im Angebot als gestrichene Position' : undefined}
                    />
                    <input value={r.position} onChange={(e) => updateLvRow(r.id, { position: e.target.value })} className={inputClass} placeholder="Pos" title={r.position || undefined} />
                    <AutoGrowTextarea
                      value={r.bezeichnung}
                      onChange={(v) => updateLvRow(r.id, { bezeichnung: v })}
                      className={`${inputClass} col-span-2 md:col-span-1 resize-none leading-snug`}
                      placeholder="Leistung"
                    />
                    <input value={r.einheit} onChange={(e) => updateLvRow(r.id, { einheit: e.target.value })} className={inputClass} placeholder="Einh." title={r.einheit || undefined} />
                    <input
                      type="text"
                      inputMode="decimal"
                      value={r.mengeInput ?? (Number.isFinite(r.menge) ? String(r.menge).replace('.', ',') : '')}
                      onChange={(e) => updateLvRow(r.id, { mengeInput: e.target.value, menge: parseTenderQuantity(e.target.value) })}
                      onBlur={() => updateLvRow(r.id, { mengeInput: undefined })}
                      className={inputClass}
                      placeholder="Menge"
                      title="Komma oder Punkt, Einheiten dürfen mitgeschrieben werden (z. B. „12,5 m²“)"
                    />
                    <AutoGrowTextarea
                      value={r.abschnitt}
                      onChange={(v) => updateLvRow(r.id, { abschnitt: v })}
                      className={`${inputClass} col-span-2 md:col-span-1 resize-none leading-snug`}
                      placeholder="Abschnitt/Sektion"
                    />
                    <button onClick={() => removeLvRow(r.id)} className="p-1.5 mt-0.5 self-start text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all justify-self-end" title="Zeile entfernen">
                      <Trash2 className="w-4 h-4" />
                    </button>
                    {(KIND_BADGE[r.kind] || r.duplicate || struck || r.needsReview || r.nep) && (
                      <div className="col-span-2 md:col-span-7 flex items-center gap-2 flex-wrap">
                        {struck ? (
                          <span className="px-1.5 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-500 border-slate-200 line-through">
                            Gestrichen
                          </span>
                        ) : r.duplicate ? (
                          <span className="px-1.5 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700 border-amber-200">
                            Duplikat
                          </span>
                        ) : KIND_BADGE[r.kind] ? (
                          <span className={`px-1.5 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider ${KIND_BADGE[r.kind]!.className}`}>
                            {KIND_BADGE[r.kind]!.label}
                          </span>
                        ) : r.needsReview ? (
                          <span className={`px-1.5 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider ${REVIEW_BADGE.className}`}>
                            {REVIEW_BADGE.label}
                          </span>
                        ) : null}
                        {r.nep && (
                          <span className="px-1.5 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider bg-violet-50 text-violet-700 border-violet-200" title="Bedarfs-/Eventualposition — zählt nicht in die Angebotssumme">
                            NEP
                          </span>
                        )}
                        <span className="text-[11px] text-slate-500">
                          {struck
                            ? 'Steht im Angebot bereits als gestrichene Position — die Streichung bleibt bestehen. Zum Anbieten dort die Streichung aufheben.'
                            : r.note}
                        </span>
                      </div>
                    )}
                    <div className="col-span-2 md:col-span-7 flex items-center gap-2">
                      {/* Reihenfolge korrigieren, BEVOR die Zeilen ins Angebot
                          gehen: aus ihr entsteht dort die Positionsnummer. */}
                      <div className="flex items-center gap-0.5 shrink-0" title="Reihenfolge ändern">
                        <button
                          type="button"
                          onClick={() => moveLvRow(r.id, -1)}
                          disabled={lvRows[0]?.id === r.id}
                          className="p-1 text-slate-300 hover:text-slate-600 hover:bg-white rounded transition-all disabled:opacity-30 disabled:hover:bg-transparent"
                          title="Zeile nach oben"
                        >
                          <ArrowUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => moveLvRow(r.id, 1)}
                          disabled={lvRows[lvRows.length - 1]?.id === r.id}
                          className="p-1 text-slate-300 hover:text-slate-600 hover:bg-white rounded transition-all disabled:opacity-30 disabled:hover:bg-transparent"
                          title="Zeile nach unten"
                        >
                          <ArrowDown className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <span className="text-[11px] uppercase font-bold text-slate-400 tracking-wider shrink-0">Zuordnung</span>
                      <select
                        value={r.assignment}
                        onChange={(e) => updateLvRow(r.id, { assignment: e.target.value as ServiceAssignment })}
                        className="px-2 py-1 bg-white border border-slate-200 rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-500/20"
                        title="Leistungsart — bestimmt die Modul-Zuordnung, wenn aus dem Angebot ein Projekt wird"
                      >
                        {ASSIGNMENTS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                      </select>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Review — Anforderungen */}
          {phase === 'review' && !isLv && (
            <div className="space-y-3">
              <p className="text-xs text-slate-500 px-1">Haken setzen bei allem, was ins Angebot übernommen werden soll. Werte sind vor der Übernahme korrigierbar.</p>
              {([
                ['title', 'Bauvorhaben (Titel)', 'text'],
                ['siteStreet', 'Straße', 'text'],
                ['siteZip', 'PLZ', 'text'],
                ['siteCity', 'Ort', 'text'],
                ['projectStart', 'Baubeginn', 'date'],
                ['projectEnd', 'Bauende', 'date'],
                ['intervalLabel', 'Begehungs-Intervall', 'text'],
                ['inspectionCount', 'Anzahl Begehungen', 'number'],
                ['tenderRequirements', 'Anforderungen (Freitext)', 'textarea'],
              ] as [ReqFieldKey, string, string][]).map(([key, lbl, type]) => {
                const has = req[key] !== undefined && req[key] !== '';
                return (
                  <div key={key} className={`flex items-start gap-3 p-3 rounded-xl border ${reqAccept[key] ? 'bg-slate-50 border-slate-100' : 'bg-white border-slate-100'}`}>
                    <input
                      type="checkbox"
                      checked={!!reqAccept[key]}
                      disabled={!has}
                      onChange={(e) => setReqAccept((a) => ({ ...a, [key]: e.target.checked }))}
                      className="mt-2 w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-400/40 disabled:opacity-40"
                    />
                    <div className="flex-1 min-w-0">
                      <label className={labelClass}>{lbl}{!has && <span className="ml-2 normal-case tracking-normal text-slate-300 font-normal">— nicht erkannt</span>}</label>
                      {type === 'textarea' ? (
                        <textarea
                          value={(req[key] as string) ?? ''}
                          onChange={(e) => setReq((r) => ({ ...r, [key]: e.target.value }))}
                          rows={4}
                          className={`${inputClass} mt-1 resize-y`}
                          placeholder="—"
                        />
                      ) : (
                        <input
                          type={type}
                          value={type === 'number' ? (Number.isFinite(req[key] as number) ? (req[key] as number) : '') : ((req[key] as string) ?? '')}
                          onChange={(e) => setReq((r) => ({ ...r, [key]: type === 'number' ? (e.target.value === '' ? undefined : Number(e.target.value)) : e.target.value }))}
                          className={`${inputClass} mt-1`}
                          placeholder="—"
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Fuss */}
        {phase === 'review' && (
          <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50">
            <Button variant="ghost" onClick={onClose} className="h-10 text-sm">Abbrechen</Button>
            {isLv ? (
              <Button onClick={applyLv} disabled={selectedCount === 0} icon={<CheckCircle2 className="w-4 h-4" />} className="h-10 text-sm">
                {selectedCount} Position(en) übernehmen
              </Button>
            ) : (
              <Button onClick={applyRequirements} icon={<CheckCircle2 className="w-4 h-4" />} className="h-10 text-sm">
                Auswahl übernehmen
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
